// Certificado de assinaturas — página própria no fim do PDF do contrato
// (pedido 2026-10-14, modelo enviado pelo usuário): fundo verde-claro com
// guilhochê, faixa lateral verde, código do documento + QR, e cada
// assinante com nome, papel, CPF/CNPJ e a assinatura em letra cursiva.
// Datas na Hora Legal Brasileira obtida do NTP.br pelo backend (o
// signedAt já é gravado com a hora do NTP.br — ver contract-signing.service
// no backend; aqui só o "gerado em").
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import qrcode from "qrcode-generator";
import { gpsDriveContractSigningService } from "@/services/gpsdrive-contract-signing.service";
import type { ClientContract } from "@/services/gpsdrive-client-contract.service";

export interface CertificateSigner {
  name: string;
  role: "signatario" | "testemunha";
  cpfCnpj?: string | null;
}

const GREEN = "#2f9e5b";
const LINE = "#c9e3d6";

// Guilhochê (linhas onduladas finas) como os papéis de certificado.
const GUILLOCHE = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='48' viewBox='0 0 160 48'>` +
    `<g fill='none' stroke='#bfe0cf' stroke-width='0.6' opacity='0.75'>` +
    `<path d='M0 12 C20 0 60 24 80 12 S140 0 160 12'/>` +
    `<path d='M0 24 C20 12 60 36 80 24 S140 12 160 24'/>` +
    `<path d='M0 36 C20 24 60 48 80 36 S140 24 160 36'/>` +
    `<path d='M0 6 C30 26 50 -2 80 18 S130 34 160 6'/>` +
    `<path d='M0 30 C30 50 50 22 80 42 S130 58 160 30'/>` +
    `</g></svg>`,
)}")`;

const STRIPE =
  "repeating-linear-gradient(45deg, rgba(47,158,91,0.85) 0 1.5px, transparent 1.5px 4px)," +
  "repeating-linear-gradient(-45deg, rgba(47,158,91,0.85) 0 1.5px, transparent 1.5px 4px)," +
  "#bfe6cc";

const brasilia = (d: Date) => {
  const parts = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")}:${get("second")}`;
};

function QrCode({ value, size = 76 }: { value: string; size?: number }) {
  const { path, count } = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(value);
    qr.make();
    const n = qr.getModuleCount();
    let d = "";
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c} ${r}h1v1h-1z`;
    return { path: d, count: n };
  }, [value]);
  return (
    <svg width={size} height={size} viewBox={`-1 -1 ${count + 2} ${count + 2}`} shapeRendering="crispEdges">
      <rect x={-1} y={-1} width={count + 2} height={count + 2} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}

function SignerRow({
  name,
  role,
  doc,
  signed,
}: {
  name: string;
  role: string | null;
  doc: string | null | undefined;
  signed: boolean;
}) {
  return (
    <div style={{ borderBottom: `1px solid ${LINE}`, padding: "14px 0 10px" }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 45%", minWidth: 0, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
          {name || "—"}
        </div>
        <div style={{ flex: "1 1 30%", minWidth: 0, fontSize: 11, fontWeight: 700 }}>{role ? `(${role})` : ""}</div>
        <div style={{ flex: "0 0 120px", fontSize: 11, lineHeight: 1.6 }}>
          <div>{doc || "—"}</div>
          <div style={{ fontWeight: 700 }}>CPF/CNPJ</div>
        </div>
      </div>
      <div style={{ minHeight: 34, marginTop: 2, display: "flex", alignItems: "flex-end" }}>
        {signed ? (
          <span style={{ fontFamily: '"Sacramento", cursive', fontSize: 26, lineHeight: 1, color: "#111" }}>
            {name.toLocaleLowerCase("pt-BR")}
          </span>
        ) : (
          <span style={{ fontSize: 11, fontStyle: "italic", color: "#8a9a92" }}>Aguardando assinatura</span>
        )}
      </div>
    </div>
  );
}

export function ContractSignatureCertificate({
  contract,
  title,
  clientName,
  clientDoc,
  signers,
  companyLogo,
  companyName,
}: {
  contract: ClientContract;
  title: string;
  clientName: string;
  clientDoc: string;
  signers: CertificateSigner[];
  companyLogo: string | null;
  companyName: string | null;
}) {
  const { data: ntp } = useQuery({
    queryKey: ["ntp-br-time"],
    queryFn: async () => ({ ...(await gpsDriveContractSigningService.getNtpTime()), fetchedAt: Date.now() }),
    staleTime: 60_000,
    retry: 1,
  });
  // Hora do NTP.br + o tempo que passou desde a consulta (cache de 1 min).
  const generatedAt = ntp?.time
    ? new Date(new Date(ntp.time).getTime() + (Date.now() - ntp.fetchedAt))
    : new Date();

  const signed = !!contract.signedAt;
  const clientSignedName =
    contract.signedMethod === "link" && contract.signedByName ? contract.signedByName : clientName;
  const verifyUrl =
    contract.signingToken && typeof window !== "undefined"
      ? `${window.location.origin}/contrato/assinar/${contract.signingToken}`
      : null;
  const timeSource = contract.signedTimeSource;
  const ntpSigned = !!timeSource && timeSource !== "servidor";

  return (
    <div
      className="cc-certificate-page"
      style={{
        position: "relative",
        marginTop: 32,
        breakBefore: "page",
        pageBreakBefore: "always",
        background: `${GUILLOCHE}, #eef8f3`,
        border: `1px solid ${LINE}`,
        paddingLeft: 22,
        color: "#111",
        fontFamily: "Arial, Helvetica, sans-serif",
      }}
    >
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 14, background: STRIPE }} />

      <div style={{ padding: "14px 22px 18px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            paddingBottom: 10,
            borderBottom: `1px solid ${LINE}`,
          }}
        >
          <div style={{ flex: "0 0 120px" }}>
            {companyLogo && <img src={companyLogo} alt={companyName ?? ""} style={{ maxHeight: 40, maxWidth: 120, objectFit: "contain" }} />}
          </div>
          <div style={{ flex: 1, textAlign: "center", fontSize: 12 }}>
            Certificado de assinaturas gerado em {brasilia(generatedAt)}
            <div style={{ fontSize: 9, color: "#5b6f66" }}>
              Hora Legal Brasileira{ntp?.server ? ` · NTP.br (${ntp.server})` : ""}
            </div>
          </div>
          <div style={{ flex: "0 0 120px", textAlign: "right", fontSize: 13, fontWeight: 800, color: GREEN }}>
            {companyName}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "18px 0",
            borderBottom: `1px solid ${LINE}`,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>{title}</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>Código do documento: {contract.documentCode ?? "—"}</div>
          </div>
          <QrCode value={verifyUrl ?? `Código do documento: ${contract.documentCode ?? ""}`} />
        </div>

        <div style={{ fontSize: 15, fontWeight: 700, marginTop: 16 }}>Assinaturas</div>

        <SignerRow name={clientSignedName} role="Contratante" doc={clientDoc} signed={signed} />
        {signers.map((s, i) => (
          <SignerRow
            key={i}
            name={s.name}
            role={s.role === "testemunha" ? "Testemunha" : "Contratada/Representante Legal"}
            doc={s.cpfCnpj}
            signed={signed}
          />
        ))}

        {signed && (
          <div style={{ marginTop: 16, fontSize: 10, lineHeight: 1.6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Registro da assinatura</div>
            <div>
              <b>Data/hora:</b> {brasilia(new Date(contract.signedAt!))} (horário de Brasília)
            </div>
            <div>
              <b>Fonte da hora:</b>{" "}
              {ntpSigned
                ? `NTP.br — servidor ${timeSource} (NIC.br, Hora Legal Brasileira)`
                : timeSource === "servidor"
                  ? "relógio do servidor (NTP.br indisponível no momento)"
                  : "relógio do servidor"}
            </div>
            {contract.signedMethod === "link" && <div><b>Meio:</b> link de assinatura eletrônica com foto e geolocalização</div>}
            {contract.signedLatitude != null && contract.signedLongitude != null && (
              <div>
                <b>Localização:</b> {contract.signedLatitude.toFixed(6)}, {contract.signedLongitude.toFixed(6)}
              </div>
            )}
            {contract.signedIp && <div><b>IP:</b> {contract.signedIp}</div>}
            {contract.signedUserAgent && (
              <div style={{ wordBreak: "break-word" }}>
                <b>Dispositivo:</b> {contract.signedUserAgent}
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 16, fontSize: 9, color: "#5b6f66" }}>
          Datas e horários deste certificado seguem a Hora Legal Brasileira, obtida dos servidores NTP.br
          (NIC.br), sincronizados com o Observatório Nacional.
        </div>
      </div>
    </div>
  );
}
